import { toGlobalID } from '../../../../../shared/helpers';
import { SHARED_EFFECTS_CATEGORY_TOOL_ID } from '../../constants';
import { ToolType } from '../../types';

/**
 * Configuration for a preset type within a tool
 */
export interface PresetTypeConfig {
    /** Unique identifier for this preset type */
    id: string;
    /** Display label */
    label: string;
    /** GraphQL presetType filter (if applicable) */
    presetType?: string;
    /** Whether this type has sub-categories to show */
    hasCategories?: boolean;
    /** Tool ID to use for category fetching (defaults to current tool) */
    categoryToolId?: string;
    /** Whether this is a mandatory field (no "None" option) */
    mandatory?: boolean;
    /** Icon name for the badge (optional) */
    icon?: string;
    /** Whether to allow custom image upload (defaults to true) */
    allowUpload?: boolean;
    /** Whether to show the "None" option (defaults to true, false if mandatory) */
    allowNone?: boolean;
    /** Whether multiple selections are allowed (defaults to false) */
    multiSelect?: boolean;
    /** Whether to hide labels below the preset thumbnails (defaults to false) */
    hideLabels?: boolean;
    /** Whether to show preset name as text inside the thumbnail instead of an image (defaults to false) */
    previewAsText?: boolean;
    /** Default preset ID to pre-select when the tool is loaded */
    defaultPresetId?: string;
}

/**
 * Tool-level configuration
 */
/**
 * Preset type configurations for each tool
 * This defines the badge row structure for the unified AI Studio
 */
export const TOOL_PRESET_CONFIG: Record<ToolType, PresetTypeConfig[]> = {
    'virtual-tryon': [
        {
            id: 'model',
            label: 'Model',
            presetType: 'person',
            mandatory: true,
            hasCategories: true,
            categoryToolId: 'virtual-tryon',
            defaultPresetId: toGlobalID('645', 'AIPresetDTO')
        },
        {
            id: 'background',
            label: 'Background',
            presetType: 'background',
            hasCategories: true,
            categoryToolId: 'virtual-tryon'
        },
        {
            id: 'pose',
            label: 'Pose',
            presetType: 'pose',
            allowUpload: false,
            hasCategories: true,
            categoryToolId: 'virtual-tryon'
        },
        {
            id: 'effect',
            label: 'Effect',
            presetType: 'effect',
            hasCategories: true,
            categoryToolId: SHARED_EFFECTS_CATEGORY_TOOL_ID,
            allowUpload: false
        }
    ],
    'product-staging': [
        {
            id: 'background',
            label: 'Background',
            presetType: 'background',
            hasCategories: true,
            allowNone: false
        },
        {
            id: 'surface',
            label: 'Surface',
            presetType: 'surface',
            allowUpload: false
        },
        {
            id: 'mood',
            label: 'Mood',
            presetType: 'mood',
            allowUpload: false
        },
        {
            id: 'effect',
            label: 'Effect',
            presetType: 'effect',
            hasCategories: true,
            categoryToolId: SHARED_EFFECTS_CATEGORY_TOOL_ID,
            allowUpload: false
        }
    ],
    'magic-swap': [
        {
            id: 'material',
            label: 'Material',
            presetType: 'material'
        },
        {
            id: 'color',
            label: 'Color',
            presetType: 'color',
            hasCategories: true
        },
        {
            id: 'effect',
            label: 'Effect',
            presetType: 'effect',
            hasCategories: true,
            categoryToolId: SHARED_EFFECTS_CATEGORY_TOOL_ID,
            allowUpload: false
        }
    ],
    'multi-image-fusion': [
        {
            id: 'layout',
            label: 'Layout',
            presetType: 'layout',
            allowUpload: false
        },
        {
            id: 'mood',
            label: 'Mood',
            presetType: 'mood',
            allowUpload: false
        },
        {
            id: 'effect',
            label: 'Effect',
            presetType: 'effect',
            hasCategories: true,
            categoryToolId: SHARED_EFFECTS_CATEGORY_TOOL_ID,
            allowUpload: false
        }
    ],
    'ad-generator': [
        {
            id: 'format',
            label: 'Format',
            presetType: 'format',
            allowUpload: false
        },
        {
            id: 'style',
            label: 'Style',
            presetType: 'style',
            allowUpload: false
        },
        {
            id: 'effect',
            label: 'Effect',
            presetType: 'effect',
            hasCategories: true,
            categoryToolId: SHARED_EFFECTS_CATEGORY_TOOL_ID,
            allowUpload: false
        }
    ],
    'video-generation': [
        {
            id: 'animation',
            label: 'Animation',
            presetType: 'animation',
            hasCategories: true,
            allowUpload: false,
            allowNone: true
        },
        {
            id: 'effect',
            label: 'Effect',
            presetType: 'effect',
            hasCategories: true,
            categoryToolId: SHARED_EFFECTS_CATEGORY_TOOL_ID,
            allowUpload: false
        }
    ],
    upscaler: [
        {
            id: 'factor',
            label: 'Scale',
            presetType: 'factor',
            allowUpload: false,
            mandatory: true,
            allowNone: false,
            hideLabels: true,
            previewAsText: true
        }
    ],
    'text-to-3d': [],
    'image-to-3d': [],
    'multi-views-to-3d': []
};

/**
 * Get preset type configuration for a tool
 */
export const getPresetTypesForTool = (toolId: ToolType): PresetTypeConfig[] => {
    return TOOL_PRESET_CONFIG[toolId] || [];
};

/**
 * Get a specific preset type config
 */
export const getPresetTypeConfig = (toolId: ToolType, presetTypeId: string): PresetTypeConfig | undefined => {
    const types = TOOL_PRESET_CONFIG[toolId];
    return types?.find((t) => t.id === presetTypeId);
};
